import {
    CAPABILITIES,
    EVENT_TYPES,
    PROTOCOL_VERSION,
    SDK_VERSION,
} from "./constants.js";
import { initLifecycle, eventMatchesScope } from "./lifecycle.js";
import { on } from "./events.js";
import { publishLocalEvent, subscribeLocalEvent } from "./event-bus.js";
import {
    getCapabilities,
    getConfig,
    getContext,
    getParams,
    getRoute,
    normalizeScope,
    scopeKey,
} from "./runtime.js";
import { notify, request, isNative } from "./transport.js";
import { requireCapability } from "./guards.js";
import {
    getStoreValue,
    removeStoreValue,
    setStoreValue,
    subscribeStore,
} from "./store.js";

function assertObject(value, message) {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        throw new TypeError(message);
    }
}

function assertScope(scope) {
    assertObject(scope, "Shell.scoped() requires a scope object.");

    if (!scope.mfe) {
        throw new Error("Shell.scoped() requires scope.mfe.");
    }

    if (!scope.version) {
        throw new Error("Shell.scoped() requires scope.version.");
    }
}

function normalizeHttpRequest(input, methodOverride) {
    assertObject(input, "Shell HTTP APIs require an object request.");

    const method = (methodOverride || input.method || "GET").toUpperCase();

    return {
        domain: input.domain,
        path: input.path || "/",
        method,
        query: input.query || {},
        body: input.body ?? null,
        headers: input.headers || {},
        timeoutMs: input.timeoutMs,
        meta: input.meta || {},
    };
}

function normalizeNavigationRequest(input) {
    assertObject(input, "shell.navigation.execute() requires an object request.");

    return {
        intent: input.intent,
        payload: input.payload || {},
        options: input.options || {},
    };
}

function normalizeExternalRequest(input) {
    assertObject(input, "shell.navigation.openExternal() requires an object request.");

    return {
        url: input.url,
        target: input.target || "external",
        replace: !!input.replace,
        meta: input.meta || {},
    };
}

function normalizeNativeRequest(input) {
    assertObject(input, "shell.navigation.openNative() requires an object request.");

    return {
        screen: input.screen,
        params: input.params || {},
        meta: input.meta || {},
    };
}

function normalizeShellError(error) {
    if (error instanceof Error) {
        return {
            code: error.code || "UNKNOWN_ERROR",
            message: error.message,
            details: error.details,
        };
    }

    if (error && typeof error === "object") {
        return {
            code: error.code || "UNKNOWN_ERROR",
            message: error.message || "Unknown Shell error",
            details: error.details,
        };
    }

    return {
        code: "UNKNOWN_ERROR",
        message: String(error || "Unknown Shell error"),
    };
}

function buildContextApi(scope) {
    return {
        get() {
            return getContext(scope);
        },

        subscribe(callback) {
            return on(EVENT_TYPES.SHELL_CONTEXT, (context) => {
                if (eventMatchesScope(context, scope)) {
                    callback(context);
                }
            });
        },
    };
}

function buildCapabilitiesApi(scope) {
    return {
        get() {
            return getCapabilities(scope);
        },

        has(capability) {
            return !!getCapabilities(scope)?.[capability];
        },

        subscribe(callback) {
            return on(EVENT_TYPES.SHELL_CAPABILITIES_CHANGED, (payload) => {
                if (eventMatchesScope(payload, scope)) {
                    callback(getCapabilities(scope));
                }
            });
        },
    };
}

function buildLifecycleApi(scope) {
    return {
        ready(payload = {}) {
            notify(EVENT_TYPES.MFE_LIFECYCLE_READY, payload, { source: scope });
        },

        error(error) {
            notify(EVENT_TYPES.MFE_ERROR_REPORT, normalizeShellError(error), {
                source: scope,
            });
        },

        routeChanged(route, params = {}) {
            notify(
                EVENT_TYPES.MFE_ROUTE_CHANGED,
                {
                    route,
                    params,
                },
                { source: scope }
            );
        },
    };
}

function buildNavigationApi(scope) {
    return {
        execute(input) {
            requireCapability(scope, CAPABILITIES.NAVIGATION);

            return request(
                EVENT_TYPES.PLATFORM_NAVIGATION_EXECUTE,
                normalizeNavigationRequest(input),
                { source: scope }
            );
        },

        back(options = {}) {
            requireCapability(scope, CAPABILITIES.NAVIGATION);

            return request(
                EVENT_TYPES.PLATFORM_NAVIGATION_BACK,
                { options },
                { source: scope }
            );
        },

        close(options = {}) {
            requireCapability(scope, CAPABILITIES.CLOSE);

            return request(
                EVENT_TYPES.PLATFORM_NAVIGATION_CLOSE,
                { options },
                { source: scope }
            );
        },

        openExternal(input) {
            requireCapability(scope, CAPABILITIES.EXTERNAL_NAVIGATION);

            return request(
                EVENT_TYPES.PLATFORM_NAVIGATION_OPEN_EXTERNAL,
                normalizeExternalRequest(input),
                { source: scope }
            );
        },

        openNative(input) {
            requireCapability(scope, CAPABILITIES.NATIVE_NAVIGATION);

            return request(
                EVENT_TYPES.PLATFORM_NAVIGATION_OPEN_NATIVE,
                normalizeNativeRequest(input),
                { source: scope }
            );
        },
    };
}

function buildHttpApi(scope) {
    const send = (input, methodOverride) => {
        requireCapability(scope, CAPABILITIES.HTTP);

        const httpRequest = normalizeHttpRequest(input, methodOverride);

        return request(EVENT_TYPES.PLATFORM_HTTP_REQUEST, httpRequest, {
            source: scope,
            timeoutMs: httpRequest.timeoutMs,
        });
    };

    return {
        request(input) {
            return send(input);
        },

        get(input) {
            return send(input, "GET");
        },

        post(input) {
            return send(input, "POST");
        },

        put(input) {
            return send(input, "PUT");
        },

        patch(input) {
            return send(input, "PATCH");
        },

        delete(input) {
            return send(input, "DELETE");
        },
    };
}

function buildAnalyticsApi(scope) {
    return {
        track(event, payload = {}) {
            requireCapability(scope, CAPABILITIES.ANALYTICS);

            notify(
                EVENT_TYPES.MFE_ANALYTICS_TRACK,
                {
                    event,
                    payload,
                },
                { source: scope }
            );
        },
    };
}

function buildStoreApi(scope) {
    return {
        get(key) {
            requireCapability(scope, CAPABILITIES.STORAGE);
            return getStoreValue(key);
        },

        set(key, value) {
            requireCapability(scope, CAPABILITIES.STORAGE);
            setStoreValue(key, value);
        },

        remove(key) {
            requireCapability(scope, CAPABILITIES.STORAGE);
            removeStoreValue(key);
        },

        subscribe(key, callback) {
            requireCapability(scope, CAPABILITIES.STORAGE);
            return subscribeStore(key, callback);
        },
    };
}

function buildEventsApi(scope) {
    return {
        publish(event, payload) {
            requireCapability(scope, CAPABILITIES.EVENTS);
            publishLocalEvent(event, payload);
        },

        subscribe(event, callback) {
            requireCapability(scope, CAPABILITIES.EVENTS);
            return subscribeLocalEvent(event, callback);
        },
    };
}

function buildScopedApi(inputScope) {
    assertScope(inputScope);

    const scope = Object.freeze(normalizeScope(inputScope));

    return Object.freeze({
        scope,
        context: buildContextApi(scope),
        capabilities: buildCapabilitiesApi(scope),
        lifecycle: buildLifecycleApi(scope),
        navigation: buildNavigationApi(scope),
        http: buildHttpApi(scope),
        analytics: buildAnalyticsApi(scope),
        store: buildStoreApi(scope),
        events: buildEventsApi(scope),
    });
}

function buildShell() {
    return Object.freeze({
        version: SDK_VERSION,
        protocolVersion: PROTOCOL_VERSION,

        scoped(scope) {
            return buildScopedApi(scope);
        },

        isNative,

        getConfig,
        getCurrentRoute: getRoute,
        getParams,

        __debug: Object.freeze({
            scopeKey,
        }),
    });
}

if (!window.Shell) {
    initLifecycle();
    window.Shell = buildShell();
}

export const shell = window.Shell;
export default window.Shell;
